import { Dialog, DialogActions, DialogTitle } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconCloseThin } from 'src/assets/icons/CloseThin';
import { ArrowIcon } from 'src/components/ArrowIcon';
import { CUSTOM_BUTTON_VARIANT, CustomButton } from 'src/components/CustomButton';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { useStyles } from './useStyles';

export const CustomDialog = ({
  classNameRoot,
  className,
  classes,
  onClose,
  onBack,
  title,
  loading,
  children,
  onCancel,
  cancelLabel,
  onSubmit,
  submitLabel,
  submitDisabled,
  cancelDisabled,
  submitProps,
  cancelProps,
  hideActions,
  disabled,
  displayCloseIcon,
  closeTooltip,
  submitTooltip,
  displayBackButton,
  dialogActionsSlot,
  preContentSlot,
  ...props
}) => {
  const styles = useStyles({ classes });

  return (
    <Dialog
      {...props}
      classes={{
        root: classNameRoot,
        paper: cx(styles.root, className, {
          [styles.hideActions]: hideActions,
          [styles.hasDialogActionsSlot]: Boolean(dialogActionsSlot),
        }),
      }}
      onClose={disabled ? null : onClose}
    >
      <DialogTitle
        className={cx(styles.dialogTitle, { [styles.noTitle]: !title })}
        disableTypography
      >
        {displayBackButton && (
          <CustomIconButton
            className={styles.buttonBack}
            onClick={onBack}
            disabled={disabled}
            data-testid="buttonBack"
          >
            <ArrowIcon direction={ROTATE_DIRECTION.left} />
          </CustomIconButton>
        )}

        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.semiBold16}
        >
          {title}
        </CustomTypography>

        {displayCloseIcon && (
          <CustomTooltip title={closeTooltip || ''}>
            <CustomIconButton
              className={styles.buttonClose}
              onClick={onClose}
              disabled={disabled}
              data-testid="buttonClose"
            >
              <IconCloseThin />
            </CustomIconButton>
          </CustomTooltip>
        )}
      </DialogTitle>

      {preContentSlot}

      <div className={styles.scrollable}>{children}</div>

      {!hideActions && (
        <DialogActions className={styles.dialogActions}>
          {dialogActionsSlot && <div className={styles.dialogActionsSlot}>{dialogActionsSlot}</div>}

          <div className={styles.actions}>
            {onCancel && (
              <CustomButton
                className={styles.buttonCancel}
                onClick={onCancel}
                disabled={cancelDisabled || disabled}
                variant={CUSTOM_BUTTON_VARIANT.text}
                data-testid="buttonCancel"
                {...cancelProps}
              >
                {cancelLabel || 'Cancel'}
              </CustomButton>
            )}

            {onSubmit && (
              <CustomTooltip title={submitTooltip || ''}>
                <span className={styles.buttonSubmitWrapper}>
                  <CustomButton
                    className={styles.buttonSubmit}
                    onClick={onSubmit}
                    loading={loading}
                    disabled={submitDisabled || disabled}
                    data-testid="buttonSubmit"
                    {...submitProps}
                  >
                    {submitLabel || 'Submit'}
                  </CustomButton>
                </span>
              </CustomTooltip>
            )}
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
};

CustomDialog.propTypes = {
  className: PropTypes.string,
  classNameRoot: PropTypes.string,
  classes: PropTypes.shape({}),
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  title: PropTypes.node,
  loading: PropTypes.bool,
  children: PropTypes.node,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  submitDisabled: PropTypes.bool,
  cancelDisabled: PropTypes.bool,
  submitProps: PropTypes.shape({}),
  cancelProps: PropTypes.shape({}),
  hideActions: PropTypes.bool,
  disabled: PropTypes.bool,
  displayCloseIcon: PropTypes.bool,
  submitTooltip: PropTypes.node,
  closeTooltip: PropTypes.node,
  displayBackButton: PropTypes.bool,
  dialogActionsSlot: PropTypes.node,
  preContentSlot: PropTypes.node,
};

CustomDialog.defaultProps = {
  className: null,
  classNameRoot: null,
  classes: null,
  onClose: null,
  onBack: null,
  title: null,
  loading: false,
  children: null,
  onCancel: null,
  cancelLabel: null,
  onSubmit: null,
  submitLabel: null,
  submitDisabled: false,
  cancelDisabled: false,
  submitProps: null,
  cancelProps: null,
  hideActions: false,
  disabled: false,
  displayCloseIcon: false,
  submitTooltip: null,
  closeTooltip: null,
  displayBackButton: false,
  dialogActionsSlot: null,
  preContentSlot: null,
};
